import { useMemo, useState } from 'react';

export type SearchPerson = {
  id: string;
  name: string;
  birthDate?: string | null;
  deptSite?: string | null;
};

/** 목록에 한 번에 보이는 사람 수. 더 보려면 더 적는다. */
const SHOWN = 8;

/**
 * 인력을 <b>적어서 찾는</b> 칸 (사용자 지시 2026-10-14).
 *
 * <p>인력이 300명을 넘는다 — 드롭다운으로는 스크롤하다 잘못 고른다. 이름 · 생년월일 · 소속 어느
 * 것으로든 찾는다(「1980」 이나 「인사총무」 로도). 서버가 좁혀 준 후보가 있으면 칸을 누르자마자
 * 그 사람들이 먼저 선다.
 *
 * <p>목록은 칸 아래 <b>흐름 안</b>에 그린다 — 스크롤되는 표 안이라 떠 있는 목록은 잘린다
 * ({@code CcPicker} 와 같은 판단).
 */
export function PersonnelSearchInput({
  people,
  candidates = [],
  disabled = false,
  onPick,
}: {
  people: SearchPerson[];
  /** 서버가 좁힌 후보. 칸이 비었을 때 이것만, 적기 시작하면 맨 앞에 선다. */
  candidates?: SearchPerson[];
  disabled?: boolean;
  onPick: (personnelId: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const matches = useMemo(() => {
    const needle = query.replace(/\s+/g, '').toLowerCase();
    if (!needle) return candidates.slice(0, SHOWN);
    const hit = (person: SearchPerson) => person.name.replace(/\s+/g, '').toLowerCase().includes(needle)
      || (person.birthDate ?? '').replace(/-/g, '').includes(needle.replace(/[-.]/g, ''))
      || (person.deptSite ?? '').replace(/\s+/g, '').toLowerCase().includes(needle);
    const first = candidates.filter(hit);
    const seen = new Set(first.map((person) => person.id));
    return [...first, ...people.filter((person) => !seen.has(person.id) && hit(person))].slice(0, SHOWN);
  }, [query, people, candidates]);

  return (
    <div style={{ minWidth: '180px' }}>
      <input
        className="input-field"
        style={{ fontSize: '11px', padding: '2px 6px', width: '100%' }}
        value={query}
        disabled={disabled}
        placeholder={candidates.length > 0 ? `후보 ${candidates.length}명 · 이름 검색` : '이름 검색'}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
      />
      {open && matches.length > 0 && (
        <div
          style={{
            marginTop: '2px',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            background: 'var(--surface, #fff)',
            maxHeight: '180px',
            overflowY: 'auto',
          }}
        >
          {matches.map((person) => (
            <button
              key={person.id}
              type="button"
              // 누르는 순간 칸의 blur 가 목록을 닫지 않도록 — 닫히면 click 이 갈 곳이 없다.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onPick(person.id);
                setQuery('');
                setOpen(false);
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '4px 8px',
                fontSize: '12px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {person.name}
              <span style={{ color: 'var(--text-muted)' }}>
                {' '}
                {[person.birthDate, person.deptSite].filter(Boolean).join(' · ')}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
